import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import aboutData from "../../assets/data/about";
import workData from "../../assets/data/work";
import type { WorkExperience } from "../../models/WorkExperience";
import { Arrow, Footer, Header, Spark } from "../../components/SiteChrome";
import ProjectVisual from "../../components/ProjectVisual";
import "./styles.scss";

const ExperienceRow = ({ item, index }: { item: WorkExperience; index: number }) => (
  <details className="experience-row">
    <summary className="experience-summary scroll-reveal" data-reveal>
      <span className="experience-year">{item.dates.map((date) => <span key={date}>{date}</span>)}</span>
      <span className="experience-content">
        <span className="experience-role">{item.role}</span>
        <span className="experience-company"><span className={`experience-logo experience-logo--${item.logoClass}`}><img src={item.logo} alt="" loading="lazy" /></span><strong>{item.company}</strong>{item.parentCompany && <span className="experience-parent-company">{item.parentCompany}</span>}</span>
        <span className="experience-meta"><span>{item.location}</span><span>{item.employment}</span></span>
        <span className="experience-teaser">{item.detail}</span>
      </span>
      <span className="experience-action" aria-hidden="true"><span className="experience-action-word">DETAILS</span><span className="experience-action-icon">+</span></span>
    </summary>
    <div className="experience-details">
      <span className="eyebrow">SELECTED WORK / 0{index + 1}</span>
      <div className="experience-detail-body">
        <ul>{item.highlights.map((highlight) => <li key={highlight}><Arrow diagonal />{highlight}</li>)}</ul>
        <div className="experience-tools" aria-label="Tools and technologies">{item.tools.map((tool) => <span key={tool}>{tool}</span>)}</div>
        {item.story.length > 0 && (
          <details className="experience-story">
            <summary><span>The full story</span><span className="experience-story-action"><span className="experience-story-more">READ MORE</span><span className="experience-story-less">SHOW LESS</span><span className="experience-story-icon">+</span></span></summary>
            <div className="experience-story-copy">{item.story.map((section, sectionIndex) => <div key={sectionIndex}>{section.children}</div>)}</div>
          </details>
        )}
      </div>
    </div>
  </details>
);

const InfoMarquee = () => {
  const marqueeRef = useRef<HTMLDivElement>(null);
  const lastInteraction = useRef(-Infinity);
  const pointerStart = useRef<{ x: number; y: number } | null>(null);
  const dragged = useRef(false);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    const marquee = marqueeRef.current;
    const firstGroup = marquee?.querySelector<HTMLElement>(".info-marquee-group");
    if (!marquee || !firstGroup || !window.requestAnimationFrame) return;

    const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    let visible = true;
    const observer = "IntersectionObserver" in window
      ? new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; })
      : null;
    observer?.observe(marquee);

    let frame = 0;
    let previous = 0;
    const move = (time: number) => {
      if (previous && visible && !document.hidden && !reducedMotion?.matches && !paused && time - lastInteraction.current > 1800) {
        marquee.scrollLeft += Math.min(time - previous, 64) * 0.06;
        const loopWidth = firstGroup.scrollWidth;
        if (loopWidth && marquee.scrollLeft >= loopWidth) marquee.scrollLeft -= loopWidth;
      }
      previous = time;
      frame = window.requestAnimationFrame(move);
    };
    frame = window.requestAnimationFrame(move);

    return () => {
      window.cancelAnimationFrame(frame);
      observer?.disconnect();
    };
  }, [paused]);

  const markInteraction = () => { lastInteraction.current = performance.now(); };
  const togglePause = () => {
    if (paused) lastInteraction.current = -Infinity;
    setPaused(!paused);
  };

  const facts = aboutData.map((fact, index) => (
    <span className="info-fact" key={fact.subtitle}>
      <span className="info-fact-top"><span className="info-fact-label">{fact.subtitle}</span><span className="info-fact-index" aria-hidden="true">{String(index + 1).padStart(2, "0")}</span></span>
      <strong>{fact.title}</strong>
    </span>
  ));

  return (
    <section className="info-strip" aria-label="A few facts about Alexandre">
      <div className="info-strip-top">
        <span>THE PERSONAL DATA / {String(aboutData.length).padStart(2, "0")} FACTS</span>
      </div>
      <div
        className="info-marquee"
        ref={marqueeRef}
        role="region"
        tabIndex={0}
        aria-label={`Personal facts, scroll horizontally. ${paused ? "Paused; click or press Space to resume." : "Click or press Space to pause automatic movement."}`}
        title={paused ? "Click to resume" : "Click to pause"}
        onWheel={markInteraction}
        onPointerDown={(event) => { pointerStart.current = { x: event.clientX, y: event.clientY }; dragged.current = false; markInteraction(); }}
        onPointerMove={(event) => { if (event.buttons || event.pointerType === "touch") markInteraction(); }}
        onPointerUp={(event) => {
          if (pointerStart.current && Math.hypot(event.clientX - pointerStart.current.x, event.clientY - pointerStart.current.y) > 8) dragged.current = true;
          pointerStart.current = null;
          markInteraction();
        }}
        onClick={() => { if (!dragged.current) togglePause(); dragged.current = false; }}
        onKeyDown={(event) => {
          if (event.key === " " || event.key === "Enter") { event.preventDefault(); togglePause(); }
          else if (event.key === "ArrowLeft" || event.key === "ArrowRight") markInteraction();
        }}
      >
        <div className="info-marquee-group">{facts}</div>
        <div className="info-marquee-group" aria-hidden="true">{facts}</div>
      </div>
    </section>
  );
};

const projects = [
  {
    number: "01",
    name: "Food Journal",
    kind: "FOOD · PLACES · DISCOVERY",
    intro: "A place for all the good places.",
    description: "A searchable map of restaurants I've tried and want to try.",
    href: "https://food.toubiana.dev/",
    visual: "food" as const,
    action: "OPEN LIVE PROJECT",
    actionLabel: "Open Food Journal live project"
  },
  {
    number: "02",
    name: "World Clock",
    kind: "VS CODE · DEVELOPER TOOLS",
    intro: "Know the time where your teammates are.",
    description: "A VS Code extension that puts multiple time zones in the status bar. Customize each clock's label, 12 or 24-hour format, seconds, and placement.",
    href: "https://marketplace.visualstudio.com/items?itemName=AlexandreToubiana.vscode-world-clock",
    visual: "clock" as const,
    action: "GET THE EXTENSION",
    actionLabel: "Get World Clock from the VS Code Marketplace"
  }
];

const clockZones = [
  { label: "LA", timeZone: "America/Los_Angeles" },
  { label: "NY", timeZone: "America/New_York" },
  { label: "PAR", timeZone: "Europe/Paris" }
];

const WorldClockVisual = () => {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    let timeout: number;
    const scheduleNextMinute = () => {
      timeout = window.setTimeout(() => {
        setNow(new Date());
        scheduleNextMinute();
      }, 60_000 - Date.now() % 60_000);
    };
    const syncWhenVisible = () => {
      if (document.hidden) return;
      window.clearTimeout(timeout);
      setNow(new Date());
      scheduleNextMinute();
    };

    scheduleNextMinute();
    document.addEventListener("visibilitychange", syncWhenVisible);
    return () => {
      window.clearTimeout(timeout);
      document.removeEventListener("visibilitychange", syncWhenVisible);
    };
  }, []);

  return (
    <div className="world-clock-visual" aria-label="World Clock extension preview">
      <div className="world-clock-caption"><span>WORLD CLOCK / IN THE EDITOR</span><span>LOCAL TIME, EVERYWHERE</span></div>
      <div className="world-clock-editor">
        <div className="world-clock-editor-bar"><span className="world-clock-window-dots" aria-hidden="true"><i /><i /><i /></span><span>settings.json</span><span>×</span></div>
        <div className="world-clock-code" aria-hidden="true">
          <span><em>1</em><code>{"{"}</code></span>
          <span><em>2</em><code className="world-clock-code-indent-1"><b>"world-clock.timezones"</b>: [</code></span>
          <span><em>3</em><code className="world-clock-code-indent-2">{"{"} <b>"timezone"</b>: <i>"America/Los_Angeles"</i>,</code></span>
          <span><em>4</em><code className="world-clock-code-indent-3"><b>"title"</b>: <i>"LA"</i> {"}"},</code></span>
          <span><em>5</em><code className="world-clock-code-indent-2">{"{"} <b>"timezone"</b>: <i>"America/New_York"</i>,</code></span>
          <span><em>6</em><code className="world-clock-code-indent-3"><b>"title"</b>: <i>"NY"</i> {"}"},</code></span>
          <span><em>7</em><code className="world-clock-code-indent-2">{"{"} <b>"timezone"</b>: <i>"Europe/Paris"</i>,</code></span>
          <span><em>8</em><code className="world-clock-code-indent-3"><b>"title"</b>: <i>"PAR"</i> {"}"}</code></span>
          <span><em>9</em><code className="world-clock-code-indent-1">]</code></span>
          <span><em>10</em><code>{"}"}</code></span>
        </div>
        <div className="world-clock-status" aria-label="Current time in Los Angeles, New York, and Paris">
          <span className="world-clock-status-mark">⌘</span>
          <div className="world-clock-times">
            {clockZones.map(({ label, timeZone }) => <span key={label}>{label} <strong>{new Intl.DateTimeFormat("en-GB", { timeZone, hour: "2-digit", minute: "2-digit", hour12: false }).format(now)}</strong></span>)}
          </div>
        </div>
      </div>
      <a className="world-clock-source" href="https://github.com/alextoub/vscode-world-clock" target="_blank" rel="noreferrer">VIEW SOURCE <Arrow diagonal /></a>
    </div>
  );
};

type AboutSegment = { text: string; emphasis?: "bold" | "accent" };

const aboutParagraphs: AboutSegment[][] = [
  [
    { text: "I'm Alexandre, a French software engineer in Santa Monica with a " },
    { text: "master's degree", emphasis: "accent" },
    { text: " in Software Engineering from " },
    { text: "EPITA", emphasis: "bold" },
    { text: ". I build " },
    { text: "web, mobile, and TV apps", emphasis: "bold" },
    { text: ". My work spans Swift on iOS and React with TypeScript on the " },
    { text: "Disney+ TV client", emphasis: "accent" },
    { text: "." }
  ],
  [
    { text: "I care as much about how a product " },
    { text: "feels to use", emphasis: "bold" },
    { text: " as how it works. I shape the " },
    { text: "UX and UI", emphasis: "accent" },
    { text: " alongside the code, from the first interaction to the smallest detail. My " },
    { text: "product background", emphasis: "bold" },
    { text: " helps me decide what matters and carry it through to release." }
  ]
];

const aboutText = aboutParagraphs.map((paragraph) => paragraph.map(({ text }) => text).join(""));
const aboutCharacterCount = aboutText.reduce((total, paragraph) => total + paragraph.length, 0);

const renderAboutSegments = (segments: AboutSegment[], limit = Infinity) => {
  let offset = 0;
  return segments.map(({ text, emphasis }, index) => {
    const visibleText = text.slice(0, Math.max(0, limit - offset));
    offset += text.length;
    if (!visibleText) return null;
    if (!emphasis) return <span key={index}>{visibleText}</span>;
    return <strong className={`about-emphasis${emphasis === "accent" ? " about-emphasis--accent" : ""}`} key={index}>{visibleText}</strong>;
  });
};

const AboutCopy = () => {
  const streamRef = useRef<HTMLDivElement>(null);
  const [started, setStarted] = useState(false);
  const [visibleCharacters, setVisibleCharacters] = useState(0);

  useEffect(() => {
    const stream = streamRef.current;
    if (!stream) return;
    const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (reducedMotion?.matches) {
      setVisibleCharacters(aboutCharacterCount);
      return;
    }
    if (!("IntersectionObserver" in window)) {
      setStarted(true);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setStarted(true);
        observer.disconnect();
      }
    }, { threshold: 0.15 });
    observer.observe(stream);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!started) return;
    const interval = window.setInterval(() => {
      setVisibleCharacters((count) => {
        if (count + 2 >= aboutCharacterCount) {
          window.clearInterval(interval);
          return aboutCharacterCount;
        }
        return count + 2;
      });
    }, 24);
    return () => window.clearInterval(interval);
  }, [started]);

  let offset = 0;
  const typing = started && visibleCharacters < aboutCharacterCount;

  return (
    <div className="about-copy">
      <Spark className="asterisk" />
      <div className="about-stream" ref={streamRef}>
        <div className="about-stream-measure" aria-hidden="true">
          {aboutParagraphs.map((paragraph, index) => <p key={index}>{renderAboutSegments(paragraph)}</p>)}
        </div>
        <div className="about-stream-visible" aria-hidden="true">
          {aboutParagraphs.map((paragraph, index) => {
            const paragraphStart = offset;
            offset += aboutText[index].length;
            if (index > 0 && visibleCharacters < paragraphStart) return null;
            const active = typing && visibleCharacters >= paragraphStart && visibleCharacters < offset;
            return <p key={index}>{renderAboutSegments(paragraph, visibleCharacters - paragraphStart)}{active && <span className="about-caret" />}</p>;
          })}
        </div>
        <div className="about-stream-screenreader">
          {aboutText.map((paragraph, index) => <p key={index}>{paragraph}</p>)}
        </div>
      </div>
      <Link to="/contact" className="text-link scroll-reveal" data-reveal>Contact me <Arrow diagonal /></Link>
    </div>
  );
};

const HomePage = () => {
  const pageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const page = pageRef.current;
    if (!page || !("IntersectionObserver" in window) || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const elements = page.querySelectorAll<HTMLElement>("[data-reveal]");
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add("is-revealed");
        observer.unobserve(entry.target);
      });
    }, { rootMargin: "0px 0px -8% 0px", threshold: 0.08 });

    page.classList.add("has-scroll-reveal");
    elements.forEach((element) => observer.observe(element));

    return () => {
      observer.disconnect();
      page.classList.remove("has-scroll-reveal");
    };
  }, []);

  return (
  <div className="v2-site" id="top" ref={pageRef}>
    <a className="skip-link" href="#main">Skip to content</a>
    <Header />
    <main id="main">
      <section className="hero" aria-labelledby="hero-title">
        <div className="hero-meta"><span>Santa Monica, California</span></div>
        <h1 id="hero-title">Alexandre<span>Toubiana<i>.</i></span></h1>
        <div className="hero-bottom">
          <p>I build digital products<br />with purpose and personality.</p>
          <a className="round-link" href="#experience" aria-label="Explore experience"><Arrow diagonal /></a>
        </div>
        <div className="hero-rule"><span>BUILDING @ DISNEY+</span><span>SCROLL TO EXPLORE</span></div>
      </section>

      <InfoMarquee />

      <section className="experience-section page-section" id="experience" aria-labelledby="experience-title">
        <div className="section-top scroll-reveal" data-reveal><span className="eyebrow">01 / WHAT I DO</span><span className="section-note">FROM AN IDEA TO THE THING PEOPLE USE</span></div>
        <h2 id="experience-title" className="section-heading scroll-reveal scroll-reveal--heading" data-reveal>Experience<span>.</span></h2>
        <div className="experience-rows">
          <div className="experience-employer-group" role="group" aria-label="Disney experience">
            {workData.filter((item) => item.parentCompany === "Disney").map((item) => <ExperienceRow item={item} index={workData.indexOf(item)} key={item.company} />)}
          </div>
          {workData.filter((item) => item.parentCompany !== "Disney").map((item) => <ExperienceRow item={item} index={workData.indexOf(item)} key={item.company} />)}
        </div>
      </section>

      <section className="projects-section page-section" id="projects" aria-labelledby="projects-title">
        <div className="section-top scroll-reveal" data-reveal><span className="eyebrow">02 / THINGS I'VE MADE</span><span className="section-note">PERSONAL IDEAS, BUILT FOR REAL USE</span></div>
        <h2 id="projects-title" className="section-heading scroll-reveal scroll-reveal--heading" data-reveal>Projects<span>.</span></h2>
        <div className="project-list">
          {projects.map((project) => (
            <article className={`project-card project-card--${project.visual}`} key={project.number}>
              <div className="project-card-copy">
                <div className="project-card-top scroll-reveal" data-reveal><span>{project.number} / {project.kind}</span><span>INDEPENDENT PROJECT</span></div>
                <div className="project-card-main"><h3 className="scroll-reveal scroll-reveal--heading" data-reveal>{project.name}<span>.</span></h3><p className="project-card-intro scroll-reveal" data-reveal>{project.intro}</p><p className="project-card-description scroll-reveal" data-reveal>{project.description}</p></div>
                <a className="project-card-bottom scroll-reveal" data-reveal href={project.href} target="_blank" rel="noreferrer" aria-label={project.actionLabel}><span>{project.action}</span><span className="project-arrow"><Arrow diagonal /></span></a>
              </div>
              {project.visual === "food" ? <ProjectVisual /> : <WorldClockVisual />}
            </article>
          ))}
        </div>
      </section>

      <section className="about-section page-section" id="about" aria-labelledby="about-title">
        <div className="section-top scroll-reveal" data-reveal><span className="eyebrow">03 / A LITTLE ABOUT ME</span><span className="section-note">THE HUMAN BEHIND THE TABS</span></div>
        <div className="about-grid">
          <h2 id="about-title" className="scroll-reveal scroll-reveal--heading" data-reveal>The best work<br />happens <em>between</em><br />disciplines<span>.</span></h2>
          <AboutCopy />
        </div>
      </section>
    </main>
    <Footer />
  </div>
  );
};

export default HomePage;
